import React, { useState } from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/contactus.scss";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  subject: "",
  orderNumber: "",
  message: "",
};

function Contact() {
  const { brandName, contact: contactDetails } = siteConfig;
  const supportHours = contactDetails.supportHours || contactDetails.hours;
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [submitMessage, setSubmitMessage] = useState("");
  const [errors, setErrors] = useState({});

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setSubmitStatus("idle");
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.firstName.trim()) {
      nextErrors.firstName = "Please enter your first name.";
    }
    if (!formData.lastName.trim()) {
      nextErrors.lastName = "Please enter your last name.";
    }
    if (!formData.email.trim()) {
      nextErrors.email = "Please enter your email address.";
    } else if (!emailPattern.test(formData.email)) {
      nextErrors.email = "Please use a valid email address.";
    }
    if (!formData.subject) {
      nextErrors.subject = "Please choose a topic.";
    }
    if (!formData.message.trim()) {
      nextErrors.message = "Please tell us how we can help.";
    } else if (formData.message.trim().length < 10) {
      nextErrors.message = "Please add a little more detail (10 characters minimum).";
    }

    return nextErrors;
  };

  const sendRequest = async () => {
    const endpoint = contactDetails?.formEndpoint;

    if (endpoint) {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Contact request failed");
      }
      return;
    }

    await new Promise((resolve) => window.setTimeout(resolve, 700));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setSubmitStatus("error");
      setSubmitMessage("Please review the highlighted fields and try again.");
      const firstInvalidField = document.getElementById(
        Object.keys(formErrors)[0]
      );
      firstInvalidField?.focus();
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setSubmitStatus("idle");
    setSubmitMessage("");

    try {
      await sendRequest();
      setFormData(initialFormData);
      setSubmitStatus("success");
      setSubmitMessage(
        "Thank you—your message is in our queue. Our care team will reply as soon as possible."
      );
    } catch {
      setSubmitStatus("error");
      setSubmitMessage(
        `We could not send your message. Please email ${contactDetails.email} instead.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="contact">
      <section className="contact__hero" aria-labelledby="contact-heading">
        <p className="contact__eyebrow">Customer care</p>
        <h1 id="contact-heading">How can we help?</h1>
        <p>
          Product questions, order updates or a little help with sizing—our
          team is here for you.
        </p>
      </section>

      <section className="contact__service" aria-label="Customer service details">
        <div className="contact__service-intro">
          <p className="contact__eyebrow">Talk to a person</p>
          <h2>Questions are always welcome.</h2>
        </div>
        <div className="contact__service-grid">
          <article>
            <span aria-hidden="true">01</span>
            <h3>Email</h3>
            <a href={`mailto:${contactDetails.email}`}>{contactDetails.email}</a>
            <p>Best for order and product questions.</p>
          </article>
          <article>
            <span aria-hidden="true">02</span>
            <h3>Call</h3>
            <a href={`tel:${contactDetails.phone.replace(/[^+\d]/g, "")}`}>
              {contactDetails.phone}
            </a>
            <p>{supportHours}</p>
          </article>
          <article>
            <span aria-hidden="true">03</span>
            <h3>Visit or write</h3>
            <address>{contactDetails.address}</address>
            <p>Please contact us before visiting.</p>
          </article>
        </div>
      </section>

      <section className="contact__form-section" aria-labelledby="form-heading">
        <div className="contact__form-aside">
          <p className="contact__eyebrow">Send a message</p>
          <h2 id="form-heading">Tell us what’s up.</h2>
          <p>
            Include your order number when you have one. Required fields are
            marked with an asterisk.
          </p>
          <div className="contact__quick-links">
            <Link to="/shipping">Shipping information <span>↗</span></Link>
            <Link to="/returns">Returns information <span>↗</span></Link>
          </div>
        </div>

        <form className="contact__form" onSubmit={handleSubmit} noValidate>
          {submitStatus !== "idle" && (
            <div
              className={`contact__notice contact__notice--${submitStatus}`}
              role={submitStatus === "error" ? "alert" : "status"}
              aria-live="polite"
            >
              {submitMessage}
            </div>
          )}

          <div className="contact__field-row">
            <div className="contact__field">
              <label htmlFor="firstName">First name *</label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.firstName)}
                aria-describedby={errors.firstName ? "firstName-error" : undefined}
              />
              {errors.firstName && <span id="firstName-error">{errors.firstName}</span>}
            </div>
            <div className="contact__field">
              <label htmlFor="lastName">Last name *</label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.lastName)}
                aria-describedby={errors.lastName ? "lastName-error" : undefined}
              />
              {errors.lastName && <span id="lastName-error">{errors.lastName}</span>}
            </div>
          </div>

          <div className="contact__field-row">
            <div className="contact__field">
              <label htmlFor="email">Email address *</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? "email-error" : undefined}
              />
              {errors.email && <span id="email-error">{errors.email}</span>}
            </div>
            <div className="contact__field">
              <label htmlFor="phone">Phone (optional)</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="contact__field-row">
            <div className="contact__field">
              <label htmlFor="subject">Topic *</label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.subject)}
                aria-describedby={errors.subject ? "subject-error" : undefined}
              >
                <option value="">Choose a topic</option>
                <option value="order">My order</option>
                <option value="return">Return or refund</option>
                <option value="product">Product or sizing</option>
                <option value="other">Something else</option>
              </select>
              {errors.subject && <span id="subject-error">{errors.subject}</span>}
            </div>
            <div className="contact__field">
              <label htmlFor="orderNumber">Order number (optional)</label>
              <input
                id="orderNumber"
                name="orderNumber"
                type="text"
                value={formData.orderNumber}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="contact__field contact__field--message">
            <label htmlFor="message">Your message *</label>
            <textarea
              id="message"
              name="message"
              rows="6"
              value={formData.message}
              onChange={handleInputChange}
              aria-invalid={Boolean(errors.message)}
              aria-describedby={errors.message ? "message-error" : undefined}
            />
            {errors.message && <span id="message-error">{errors.message}</span>}
          </div>

          <p className="contact__privacy-note">
            By sending this form, you agree that {brandName} may use your
            details to answer your request. See our <Link to="/privacy">privacy policy</Link>.
          </p>
          <button className="contact__submit" type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <><span className="contact__spinner" aria-hidden="true" /> Sending…</>
            ) : (
              "Send message"
            )}
          </button>
        </form>
      </section>
    </main>
  );
}

export default Contact;
